import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "@/data/projects";
import { useLang } from "@/i18n/LanguageContext";

export default function Projects() {
  const { t, lang } = useLang();
  const P = t.projects;
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative border-b border-white/10 py-28 lg:py-36"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-6">
            <div className="label-mono mb-4">{P.label}</div>
            <h2 className="display-2 text-4xl md:text-5xl lg:text-6xl text-white">
              {P.title_a}
              <br />
              <span className="text-white/40">{P.title_muted}</span>{" "}
              <span className="text-[#FF3333]">{P.title_accent}</span>
            </h2>
          </div>
        </div>

        <div className="space-y-px bg-white/10">
          {PROJECTS.map((p, i) => {
            const content = lang === "ko" ? p.ko : p.en;
            const domain = lang === "ko" ? p.domain_ko : p.domain_en;
            const metricLabel = lang === "ko" ? p.metric_label_ko : p.metric_label_en;
            return (
              <motion.div
                key={p.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
              >
                <Link
                  to={`/projects/${p.code}`}
                  data-testid={`project-${p.code.toLowerCase()}`}
                  className="group grid grid-cols-12 gap-6 bg-[#101012] hover:bg-[#161618] transition-colors p-8 lg:p-12 items-center"
                >
                  <div className="col-span-12 lg:col-span-2">
                    <div className="font-mono text-[10px] tracking-widest uppercase text-[#FF3333]">
                      {p.code} / {p.year}
                    </div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 mt-2">
                      {domain}
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-5">
                    <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">
                      {content.title}
                    </h3>
                    <p className="mt-4 text-white/60 text-sm leading-relaxed">{content.blurb}</p>
                    <div className="mt-4 font-mono text-[10px] tracking-widest uppercase text-white/40 group-hover:text-[#FF3333] transition-colors">
                      {P.view_case} →
                    </div>
                  </div>

                  <div className="col-span-6 lg:col-span-2">
                    <div className="font-display font-bold text-5xl text-white">{p.metric}</div>
                    <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-white/40">
                      {metricLabel}
                    </div>
                  </div>

                  <div className="col-span-6 lg:col-span-3 relative h-32 overflow-hidden">
                    <img
                      src={p.img}
                      alt={content.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ filter: "grayscale(0.7) contrast(1.05) brightness(0.6)" }}
                    />
                    <div className="absolute top-3 right-3">
                      <div className="w-9 h-9 border border-white/40 group-hover:bg-[#FF3333] group-hover:border-[#FF3333] flex items-center justify-center transition-colors">
                        <ArrowUpRight size={16} className="text-white" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
